import type { TranscriptLine } from "shared/components/CallTranscript";
import { instance } from "./apiBase";

export type CallAnsweredByType = "human" | "ai";

export type AiHandoffReason = "timer" | "sent" | "silenced";

export interface CapabilityTokenResponse {
  identity: string;
  token: string;
  expires_at: string;
}

export interface CallSummary {
  id: number;
  status:
    | "authorized"
    | "queued"
    | "ringing"
    | "in_progress"
    | "completed"
    | "failed"
    | "no_answer"
    | "busy"
    | "canceled"
    | "expired_before_dial";
  version: number;
  direction: "outbound" | "inbound";
  from_number: string;
  to_number: string;
  // CRM-1393: set when the partner's own AI leasing agent handed an inbound
  // call to a human — the agent line the caller reached first, and its label.
  // `from_number` is then the caller; when it EQUALS `transferred_from_number`
  // the api could not tell who the caller was (see shared/utils/transferred-call).
  // Absent on responses from an api that predates the fields.
  transferred_from_number?: string | null;
  transferred_from_name?: string | null;
  lead_id: number | null;
  customer_id: number | null;
  // Partner user whose browser answered an inbound call (CRM-905); null for
  // outbound calls and unanswered inbound calls.
  answered_by_partner_user_id: number | null;
  duration_seconds: number | null;
  // CRM-1235: the canonical missed derivation, computed by
  // `BoomCRM::Call#missed?`. Never re-derive it on the client.
  missed: boolean;
  // How long the phone rang. `duration_seconds` is talk time (0 for a call
  // nobody answered), so it can never stand in for this.
  ring_seconds: number | null;
  disposition: string | null;
  recording_url: string | null;
  transcript_text: string | null;
  transcription_status: string | null;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  authorized_expires_at: string;
  created_at: string;
  team_first?: boolean;
  ai_handoff_reason?: AiHandoffReason | null;
  ai_handoff_at?: string | null;
  ai_handoff_by?: CallParty | null;
}

export interface AuthorizeCallPayload {
  to_number: string;
  idempotency_key: string;
  lead_id?: number;
  // CRM contact the call is placed from; with lead_id it pins the call's
  // identity server-side. Absent -> the api resolves it from the number.
  contact_id?: number;
  customer_id?: number;
  // Caller-selected "from" number; omitted -> backend picks the default.
  provisioned_phone_number_id?: number;
}

export interface AuthorizeCallResponse {
  call: CallSummary;
  dial_token: string;
}

export interface RecordingResponse {
  recording_url: string;
  duration_seconds: number | null;
}

export type CallTranscriptLine = TranscriptLine;

// CRM-1777: how a voicemail stopped being New — played, or marked heard after
// the team acted on its transcript.
export type VoicemailHeardVia = "played" | "marked";

// Who first heard a voicemail and how, after a play or Mark as heard.
export interface VoicemailHeardResponse {
  voicemail_heard_at: string | null;
  voicemail_heard_by_name: string | null;
  voicemail_heard_via: VoicemailHeardVia | null;
}

export interface CallTranscriptResponse {
  agent_name: string | null;
  summary: string | null;
  recording_url: string | null;
  recording_duration_seconds: number | null;
  transcript: CallTranscriptLine[];
  transcription_status: string | null;
}

export interface AnnounceCallPayload {
  to_number: string;
  message: string;
  lead_id?: number;
  contact_id?: number;
  customer_id?: number;
}

// A person referenced by a call log row. Resolved server-side via subqueries,
// so the list never pays for an N+1 or a LEFT JOIN.
export interface CallParty {
  id: number;
  name: string | null;
}

// One row of the call log (CRM-1235). Everything `CallSummary` exposes, plus
// the resolved labels the log renders instead of raw ids.
export interface CallListItem extends CallSummary {
  answered_by_type?: CallAnsweredByType;
  has_recording?: boolean;
  lead_deleted?: boolean;
  // Partner user whose browser picked the call up; null when nobody did.
  answered_by: CallParty | null;
  // Partner user who placed an outbound call.
  initiated_by: CallParty | null;
  lead: CallParty | null;
  // Friendly label for `to_number` (property name / provisioned-number name).
  to_name: string | null;
  // Friendly label for `from_number`.
  from_name: string | null;
  // Comm-service conversation the call was threaded into. `crm_calls` has no
  // such column today, so the endpoint may omit it — the Calls screen falls
  // back to the contact, then the lead, when it is absent.
  conversation_id?: string | null;
  // The CRM contact behind the external party. The inbox threads by contact, so
  // this reaches the same conversation without comm-service having to supply an
  // id boompay-api does not store.
  contact_id?: number | null;
  summary?: string | null;
  response_seconds?: number | null;
  lead_source?: string | null;
  property?: CallParty | null;
}

export type CallsHandledBy = "ai" | "team" | "missed";

export interface CallsListParams {
  page?: number;
  per_page?: number;
  search?: string;
  direction?: CallSummary["direction"];
  missed?: boolean;
  // ISO8601 instants; the endpoint filters the call's creation time.
  from?: string;
  to?: string;
  answered_by_type?: CallAnsweredByType;
  answered_by_partner_user_ids?: number[];
  property_ids?: number[];
  handled_by?: CallsHandledBy;
  team_tried_first?: boolean;
}

// Per-tab totals. The endpoint computes these BEFORE applying the direction /
// missed facets, so selecting a tab never zeroes out the others.
export interface CallsTabCounts {
  all: number;
  missed: number;
  inbound: number;
  outbound: number;
  answered_by_human: number;
  human: number;
  ai: number;
  team_tried_first?: number;
}

// The envelope `present_collection` emits for every paginated portal endpoint.
// Anything endpoint-specific rides in `extra` — here, the tab counts.
export interface CallsListResponse {
  items: CallListItem[];
  total_count: number;
  page_count: number;
  current_page: number;
  has_more: boolean;
  extra?: { counts?: CallsTabCounts };
}

// CRM-1747 live-call transfer.
export type CallTransferStatus =
  | "ringing"
  // Talk first: the teammate is speaking to the destination.
  | "consulting"
  | "answered"
  | "not_answered"
  | "canceled"
  | "failed";

export type CallTransferTargetKind = "team" | "list" | "typed";

export interface CallTransfer {
  id: number;
  call_id: number;
  status: CallTransferStatus;
  mode: "now" | "talk_first";
  target_kind: CallTransferTargetKind;
  target_name: string;
  target_number: string | null;
  target_partner_user_id: number | null;
  offset_seconds: number;
  // Talk first: the caller waits on hold; the call itself has ended.
  caller_on_hold: boolean;
  call_ended: boolean;
  started_at: string;
  answered_at: string | null;
  completed_at: string | null;
  ended_at: string | null;
}

export type CallTransferMode = CallTransfer["mode"];

export interface TransferListEntry {
  id: number;
  name: string;
  note: string | null;
  phone_number: string;
  position: number;
}

export interface TransferTargetsResponse {
  team: { id: number; name: string | null }[];
  transfer_list: TransferListEntry[];
  typed_numbers_allowed: boolean;
}

export type StartTransferPayload =
  | { target_kind: "team"; target_partner_user_id: number }
  | { target_kind: "list"; transfer_list_entry_id: number }
  | { target_kind: "typed"; phone_number: string };

export const voiceApi = {
  async getCapabilityToken(): Promise<CapabilityTokenResponse> {
    const { data } = await instance.get<CapabilityTokenResponse>(
      "/portal/v1/boom_crm/voice/capability_token"
    );
    return data;
  },

  async authorizeCall(
    payload: AuthorizeCallPayload
  ): Promise<AuthorizeCallResponse> {
    const { data } = await instance.post<AuthorizeCallResponse>(
      "/portal/v1/boom_crm/calls",
      payload
    );
    return data;
  },

  // CRM-1235 call log. Never-dialed rows (authorized / queued /
  // expired_before_dial) are excluded server-side, so the client renders
  // whatever comes back.
  async listCalls(params: CallsListParams = {}): Promise<CallsListResponse> {
    const { data } = await instance.get<CallsListResponse>(
      "/portal/v1/boom_crm/calls",
      { params }
    );
    return data;
  },

  async fetchCall(id: number): Promise<CallListItem> {
    const { data } = await instance.get<CallListItem>(
      `/portal/v1/boom_crm/calls/${id}`
    );
    return data;
  },

  async submitDisposition(
    id: number,
    disposition: string
  ): Promise<CallSummary> {
    const { data } = await instance.patch<CallSummary>(
      `/portal/v1/boom_crm/calls/${id}`,
      { disposition }
    );
    return data;
  },

  // Links a lead captured during an inbound call (CRM-905) so the recording
  // and transcript land on its activity log.
  async linkLead(id: number, leadId: number): Promise<CallSummary> {
    const { data } = await instance.patch<CallSummary>(
      `/portal/v1/boom_crm/calls/${id}`,
      { lead_id: leadId }
    );
    return data;
  },

  async getRecording(id: string): Promise<RecordingResponse> {
    const { data } = await instance.get<RecordingResponse>(
      `/portal/v1/boom_crm/calls/${id}/recording`
    );
    return data;
  },

  async getTranscript(id: number): Promise<CallTranscriptResponse> {
    const { data } = await instance.get<CallTranscriptResponse>(
      `/portal/v1/boom_crm/calls/${id}/transcript`
    );
    return data;
  },

  // Reports a play of the call's voicemail: marks it heard for the whole team
  // the first time, and adds a row to the recording-access history every time.
  async markVoicemailPlayed(
    id: number | string
  ): Promise<VoicemailHeardResponse> {
    const { data } = await instance.post<VoicemailHeardResponse>(
      `/portal/v1/boom_crm/calls/${id}/voicemail_played`
    );
    return data;
  },

  // Mark as heard: clears the voicemail's New mark for the whole team without
  // playing it, for a team that acted on the transcript. No-op once heard.
  async markVoicemailHeard(
    id: number | string
  ): Promise<VoicemailHeardResponse> {
    const { data } = await instance.post<VoicemailHeardResponse>(
      `/portal/v1/boom_crm/calls/${id}/voicemail_heard`
    );
    return data;
  },

  async sendToAi(id: number): Promise<CallSummary> {
    const { data } = await instance.post<CallSummary>(
      `/portal/v1/boom_crm/calls/${id}/send_to_ai`
    );
    return data;
  },

  async silence(id: number): Promise<void> {
    await instance.post(`/portal/v1/boom_crm/calls/${id}/silence`);
  },

  async getTransferTargets(): Promise<TransferTargetsResponse> {
    const { data } = await instance.get<TransferTargetsResponse>(
      "/portal/v1/boom_crm/call_transfer/targets"
    );
    return data;
  },

  async startTransfer(
    callId: number,
    payload: StartTransferPayload,
    mode: CallTransferMode
  ): Promise<CallTransfer> {
    const { data } = await instance.post<CallTransfer>(
      `/portal/v1/boom_crm/calls/${callId}/transfers`,
      { ...payload, mode }
    );
    return data;
  },

  // Talk first: Complete transfer / Back to caller / End call for everyone.
  async consultAction(
    callId: number,
    transferId: number,
    action: "complete" | "back" | "end"
  ): Promise<CallTransfer> {
    const { data } = await instance.post<CallTransfer>(
      `/portal/v1/boom_crm/calls/${callId}/transfers/${transferId}/${action}`
    );
    return data;
  },

  async fetchTransfer(
    callId: number,
    transferId: number
  ): Promise<CallTransfer> {
    const { data } = await instance.get<CallTransfer>(
      `/portal/v1/boom_crm/calls/${callId}/transfers/${transferId}`
    );
    return data;
  },

  async announce(payload: AnnounceCallPayload): Promise<CallSummary> {
    const { data } = await instance.post<CallSummary>(
      "/portal/v1/boom_crm/calls/announce",
      payload
    );
    return data;
  }
};
